import ApiServer from "../ApiServer.ts";
import * as FwDatas from "../FwDatas.ts";
import * as Html from "../util/Html.ts";
import * as modal from "../util/modal.ts";
import * as amplify from "../util/amplify.ts";
import * as i18n from "../i18n.ts";
import * as DateUtils from "../lib/DateUtils.ts";
import {EventStore} from "../server/EventStore.ts";
import {pickFileFromDevice, registerFileDropConsumer} from "../lib/FileUtils.ts";
import * as FeatureSeen from "../util/FeatureSeen.ts";
import {importCalendarFile} from "./icsimport.ts";
import {record} from "./aiimport_voice.ts";
import {editAsWholeSeries} from "./calendar_modal.ts";
//the Email button of Quick Add opens the email import
import "../features/aiinbox.ts";
import deviceId from "../lib/deviceId.ts";
import {OS} from "../lib/os.ts";
import FizConfig from "../FizConfig.ts";
import Icons from "../svg/Icons.ts";
import {eventImportCache} from "./manageimport.ts";
import type {AIImportKind, ExternalImportBean, IEvent} from "../server/fizapilib.auto.ts";


/**
 * The AI import being parsed by the server (FWWEB-1846). One at a time: the server answers minutes later, the
 * externalImport sync is the source of truth of its state, so it is polled until the import is FINISHED or in ERROR.
 */
type Ongoing = {
  metaId :string,
  //restarts the capture, the server does not keep the input so a failed import cannot be resubmitted
  retry :()=>void,
  processing ?:modal.DomModal,
  snack ?:{close:()=>void},
  poll ?:number
}
let ongoing :Ongoing|undefined;

const POLL_INTERVAL = 4000;
//an import still ONGOING after this delay is not resumed on page load, the server will have put it in error
const RESUME_MAX_AGE = 30*60*1000;
const STAGE_KEYS = ['reading','extracting','checking','almost'];
const STAGE_INTERVAL = 3000;
const IMAGE_MAX_SIDE = 2560;
const IMAGE_MAX_SIZE = 2*1024*1024;
//the largest file the AI parses (FWWEB-1904): above it a document is refused before upload, a picture downsized first
const FILE_MAX_SIZE = FizConfig.AI_IMPORT_MAX_FILE_SIZE;
const PHOTO_TYPES = "image/jpeg,image/png,image/heic,image/heif,image/webp";
const FILE_TYPES = PHOTO_TYPES+",application/pdf,.pdf,text/calendar,.ics";

type Modality = 'photo'|'file'|'voice';
const DISCOVERY = {
  photo:'aiimport.discovery.photo',
  file:'aiimport.discovery.file',
  voice:'aiimport.discovery.voice'
} as const;

function stillImporting(){
  modal.alert({title:'aiimport.stillimporting.title', message:'aiimport.stillimporting.body', button:[{title:'actions.gotit'}]});
}

function refreshAccountState(){
  return ApiServer.accgetstate(deviceId, "WebFirebase", FizConfig.VERSION||"", OS.getTimeZone()).then(accountState=>{
    FwDatas.setState(accountState);
  })
}

/**
 * Sends a capture to the AI and follows it until its events are on the calendar.
 * @param kind what the input is
 * @param input the picture or the document, or the text for a TEXT import
 * @param retry restarts the capture when the user asks to try again
 */
export async function startAiImport(kind :AIImportKind, input :File|string, retry :()=>void){
  if(ongoing){
    stillImporting();
    return;
  }
  if(!canImport()) return;
  //checked at submit and not when the button is clicked: the capture can take time
  if(!navigator.onLine){
    modal.oops('aiimport.offline');
    return;
  }
  const processing = await openProcessing(kind, input);
  let bean :ExternalImportBean;
  try{
    bean = await ApiServer.evtaiimport.setOptions({timeout:120000})(
      kind==='TEXT'?{kind, text:input as string}:{kind, file:input as File}
    );
  }catch(err :any){
    modal.closeDomModal(processing);
    if(err?.rightFlag==='KO_PREMIUM'){
      refreshAccountState();
      Html.trigger(null,'takepremium',['aiimport']);
    }else if(/attachement size/.test(String(err?.message||''))){
      //the server limit is lower than the configured one
      tooLarge();
    }else{
      showFailure('failed', retry);
    }
    return;
  }
  //the free tries left are read from the account state
  refreshAccountState();
  ongoing = {metaId:bean.metaId, retry};
  if(processing.isConnected){
    ongoing.processing = processing;
  }else{
    continueInBackground();
  }
  follow();
}

async function openProcessing(kind :AIImportKind, input :File|string){
  const
    thumbnail = (input instanceof File && input.type.startsWith('image/'))?URL.createObjectURL(input):undefined,
    stages = kind==='TEXT'?['listening',...STAGE_KEYS.slice(1)]:STAGE_KEYS,
    processing = modal.openDomModal(await import('./aiimport_processing.mustache'),{
      thumbnail,
      icon:kind==='TEXT'?Icons.ic_voice:Icons.ic_file,
      stage:'aiimport.processing.'+stages[0]
    }),
    label = processing.querySelector('.aiimport-stage') as HTMLElement;
  let stage = 0;
  //the server does not tell the stages: the labels move on with time and hold on the last one until the answer
  const timer = setInterval(()=>{
    if(stage<stages.length-1){
      stage++;
      label.textContent = i18n.get('aiimport.processing.'+stages[stage]);
    }
  }, STAGE_INTERVAL);
  //closing it does not stop the import, it goes on in background
  processing.onClose = ()=>{
    clearInterval(timer);
    if(thumbnail) URL.revokeObjectURL(thumbnail);
    if(ongoing?.processing===processing){
      ongoing.processing = undefined;
      continueInBackground();
    }
  };
  return processing;
}

async function continueInBackground(){
  if(!ongoing || ongoing.snack) return;
  const current = ongoing;
  const snack = await modal.toast({emoji:'✨', text:'aiimport.snackbar.ongoing'}, 0);
  snack.domNode.classList.add('aiimport-ongoing');
  current.snack = snack;
  snack.closed.then(()=>{
    if(current.snack===snack) current.snack = undefined;
  });
}

function follow(){
  const current = ongoing;
  current.poll = setInterval(async ()=>{
    const imports = await eventImportCache.refresh().catch(()=>undefined);
    const bean = imports?.find(item=>item.metaId===current.metaId);
    if(ongoing!==current || !bean || bean.runState==='ONGOING') return;
    clearInterval(current.poll);
    ongoing = undefined;
    current.snack?.close();
    EventStore.invalidate();
    amplify.publish('eventUpdated');
    amplify.publish('eventImportUpdated');
    const events = bean.runState==='FINISHED'?await eventsOf(bean.metaId):[];
    if(current.processing){
      modal.closeDomModal(current.processing);
      showResult(bean, events, current.retry);
    }else{
      toastResult(bean, events, current.retry);
    }
  }, POLL_INTERVAL) as unknown as number;
}

async function eventsOf(importId :string){
  const events = await EventStore.getFor(null);
  return events
    .filter(event=>event.externalImportId===importId)
    .sort((a,b)=>a.startDate.getTime()-b.startDate.getTime());
}

/**
 * The events found are the ones created plus the ones updated: an event already on the calendar (imported before, by
 * email or by AI) is merged into it, it stays in its first import and is not listed with this one
 */
function foundCount(bean :ExternalImportBean){
  return (bean.created||0)+(bean.updated||0);
}

function showResult(bean :ExternalImportBean, events :IEvent[], retry :()=>void){
  if(bean.runState==='ERROR'){
    showFailure('failed', retry);
  }else if(foundCount(bean)===0){
    showFailure('noevent', retry);
  }else if(events.length===0){
    modal.alert({title:'aiimport.merged.title', message:'aiimport.merged.body', button:[{title:'aiimport.success.gotocalendar'}]});
  }else{
    showSuccess(bean.metaId, events);
  }
}

async function toastResult(bean :ExternalImportBean, events :IEvent[], retry :()=>void){
  if(bean.runState==='ERROR'){
    modal.toast({emoji:'⚠️', text:'aiimport.snackbar.failure'});
    return;
  }
  const review :modal.ToastButton = {title:'aiimport.snackbar.review'};
  const count = foundCount(bean);
  const text = count===0?'aiimport.snackbar.noevent'
    :events.length===0?'aiimport.merged.title'
    :events.length===1?'aiimport.snackbar.success_one'
    :i18n.get('aiimport.snackbar.success',{count:events.length});
  const {closed} = await modal.toast({emoji:count===0?'🧐':'🎉', text, button:[review]}, 10000);
  if((await closed).button===review){
    showResult(bean, events, retry);
  }
}

async function showFailure(type :'failed'|'noevent', retry :()=>void){
  const answer = await modal.askAsPromise({
    lottie:type==='failed'?'images/lottie/dizzy_face.json':'images/lottie/face_with_monocle.json',
    title:'aiimport.'+type+'.title',
    message:'aiimport.'+type+'.body',
    button:[
      {title:'aiimport.tryagain', value:'retry'},
      {title:'aiimport.addmanually', class:'secondary', value:'manual'}
    ]
  });
  if(answer==='retry'){
    retry();
  }else if(answer==='manual'){
    Html.trigger(null,'eventcreate');
  }
}

async function showSuccess(importId :string, events :IEvent[]){
  const one = events.length===1;
  const success = modal.openDomModal(await import('./aiimport_success.mustache'),{
    importId,
    title:one?i18n.get('aiimport.success.title_one'):i18n.get('aiimport.success.title',{count:events.length}),
    subtitle:one?'aiimport.success.subtitle_one':'aiimport.success.subtitle',
    events:events.map(event=>({
      metaId:event.metaId,
      text:event.text||'',
      color:event.colorDB || (event as any).colorAttendee || event.calendar?.color || FizConfig.defaultColor,
      day:relativeDay(event.startDate),
      time:event.allDay?i18n.get('new_event.allday')
        :DateUtils.format(event.startDate,'LT')+(event.endDate?' - '+DateUtils.format(event.endDate,'LT'):''),
      where:event.where
    }))
  });
  //opened from here, an event the calendar has not shown yet has to be known for its menu
  events.forEach(event=>FwDatas.cache(event));
  success.onClose = editAsWholeSeries(events.map(event=>event.metaId));
}

/** Today, Tomorrow, else the day and the month, the year only when it is not the current one */
function relativeDay(date :Date){
  const
    day = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime(),
    now = new Date(),
    today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime(),
    oneDay = 24*3600*1000;
  if(day===today) return i18n.get('deltatime.today');
  if(day===today+oneDay) return i18n.get('deltatime.tomorrow');
  return date.toLocaleDateString(i18n.getSelectedLanguage(), {
    month:'long', day:'numeric', year:date.getFullYear()===now.getFullYear()?undefined:'numeric'
  });
}

/** Pictures are sent at most IMAGE_MAX_SIDE wide, small flyer text stays readable at that size */
async function compressImage(file :File) :Promise<File>{
  if(!/^image\/(jpeg|png|webp)$/.test(file.type)){
    //the browser cannot draw it (HEIC), the server gets it as is
    return file;
  }
  const bitmap = await createImageBitmap(file).catch(()=>undefined);
  if(!bitmap) return file;
  const scale = Math.min(1, IMAGE_MAX_SIDE/Math.max(bitmap.width, bitmap.height));
  if(scale===1 && file.size<=IMAGE_MAX_SIZE){
    bitmap.close();
    return file;
  }
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width*scale);
  canvas.height = Math.round(bitmap.height*scale);
  canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const blob = await new Promise<Blob>(resolve=>canvas.toBlob(resolve, 'image/jpeg', 0.85));
  return blob?new File([blob], file.name.replace(/\.\w+$/,'')+'.jpg', {type:'image/jpeg'}):file;
}

/** false when the member is sent to the paywall instead: not premium, or no free try left */
function canImport(){
  if(FwDatas.getPermission('aiimportupload')==='takepremium'){
    Html.trigger(null,'takepremium',['aiimport']);
    return false;
  }
  return true;
}

/**
 * The free tries left to the circle, undefined when it has no limit (premium) or when it is premium only: only the
 * freemium circles of the experiment get a count, the server sets it out of a LaunchDarkly flag.
 */
export function aiImportCredits() :{left:number,used:number,total:number}|undefined{
  const premium = FwDatas.getState()?.premium;
  if(!premium || premium.permittedNbOfAIImportByUpload==null || premium.permittedNbOfAIImportByUpload===0) return undefined;
  const total = premium.permittedNbOfAIImportByUpload, used = Math.min(total, premium.nbOfAIImportByUpload||0);
  return {left:total-used, used, total};
}

/** Introduces the modality on its first use, once per member (stored in the browser) */
async function discover(modality :Modality){
  const key = DISCOVERY[modality];
  if(FeatureSeen.hasSeen(key)) return;
  FeatureSeen.markSeen(key);
  await modal.askAsPromise({
    title:'aiimport.discovery.'+modality+'.title',
    message:'aiimport.discovery.'+modality+'.body',
    button:[{title:'aiimport.discovery.cta', value:'true'}]
  });
}

/** Shown before any upload: the file never leaves the browser and no free try is spent */
function tooLarge(){
  modal.alert({
    lottie:'images/lottie/anim_storage_full.json',
    title:'aiimport.toolarge.title',
    message:i18n.get('aiimport.toolarge.body',{size:Math.floor(FILE_MAX_SIZE/1000000)}),
    button:[{title:'actions.gotit'}]
  });
}

/** A picture is downsized first, only one the browser cannot draw (HEIC) can stay too large */
async function sendImage(file :File, retry :()=>void){
  const image = await compressImage(file);
  if(image.size>FILE_MAX_SIZE){
    tooLarge();
    return;
  }
  startAiImport('IMAGE', image, retry);
}

async function capturePhoto(){
  const files = await pickFileFromDevice(PHOTO_TYPES);
  if(files.length===0) return;
  sendImage(files[0], capturePhoto);
}

async function captureFile(){
  const files = await pickFileFromDevice(FILE_TYPES);
  if(files.length===0) return;
  routeFile(files[0], captureFile);
}

/**
 * A calendar file goes through the classic ICS import, free and without AI, the other files are parsed by the AI.
 */
async function routeFile(file :File, retry :()=>void){
  if(file.type==='text/calendar' || /\.ics$/i.test(file.name)){
    importCalendarFile(file);
  }else if(file.type.startsWith('image/')){
    sendImage(file, retry);
  }else if(file.size>FILE_MAX_SIZE){
    tooLarge();
  }else if(file.type==='application/pdf' || /\.pdf$/i.test(file.name)){
    startAiImport('DOCUMENT', file, retry);
  }else{
    modal.oops('aiimport.unsupported');
  }
}

/**
 * A file dropped on the calendar or on the drop zone of the import modal: it skips the form, not the rules, the
 * paywall and the discovery come first as for a click.
 */
export async function importDroppedFile(file :File){
  if(file.type==='text/calendar' || /\.ics$/i.test(file.name)){
    importCalendarFile(file);
    return;
  }
  if(ongoing){
    stillImporting();
    return;
  }
  if(!canImport()) return;
  await discover('file');
  routeFile(file, captureFile);
}

/** Opens a capture from a button, the paywall and the discovery first */
async function openCapture(button :HTMLElement, modality :Modality, capture :()=>void){
  //the capture replaces the manual creation of the event, or the import modal
  const fromModal = button?.closest('.modal-container') as modal.DomModal;
  if(fromModal) modal.closeDomModal(fromModal);
  if(ongoing){
    stillImporting();
    return;
  }
  if(!canImport()) return;
  await discover(modality);
  capture();
}

/** An import still parsing when the page was left is followed again, with its snackbar */
export async function resumeAiImport(){
  if(ongoing) return;
  const
    accountId = String(FwDatas.getUser().accountId),
    imports = await eventImportCache.need(),
    bean = imports.find(item=>item.importType==='EVENT_BY_AI_UPLOAD' && item.runState==='ONGOING'
      && String(item.accountId)===accountId
      && Date.now()-(item.creationDate?.getTime()||0)<RESUME_MAX_AGE);
  if(!bean || ongoing) return;
  ongoing = {metaId:bean.metaId, retry:()=>Html.trigger(null,'eventcreate')};
  continueInBackground();
  follow();
}

Html.registerDom({
  aiimportphoto(){
    openCapture(this, 'photo', capturePhoto);
  },
  aiimportfile(){
    openCapture(this, 'file', captureFile);
  },
  async aiimportvoice(){
    //as the design: the recorder floats over the calendar, in place of the New Event modal
    const eventModal = this.closest('.modal-container') as modal.DomModal;
    if(eventModal) modal.closeDomModal(eventModal);
    if(ongoing){
      stillImporting();
      return;
    }
    if(!canImport()) return;
    await discover('voice');
    record(document.body, transcript=>{
      if(!navigator.onLine){
        modal.oops('aiimport.offline');
        return;
      }
      startAiImport('TEXT', transcript, ()=>Html.trigger(null,'eventcreate'));
    });
  },
  aiimportstill(){
    stillImporting();
  },
  aiimportcredits(){
    Html.trigger(null,'takepremium',['aiimport']);
  },
  async aiimportundo(importId :string){
    const count = (await eventsOf(importId)).length;
    const answer = await modal.askAsPromise({
      title:'aiimport.undo.title',
      message:count===1?'aiimport.undo.body_one':i18n.get('aiimport.undo.body',{count}),
      button:[
        {title:'aiimport.undo.delete', class:'negative', value:'true'},
        {title:'actions.cancel', class:'secondary', value:'false'}
      ]
    });
    if(answer!=='true') return;
    await ApiServer.evtexternalimportdelete(true, [importId]);
    modal.closeDomModal();
    EventStore.invalidate();
    eventImportCache.invalidate();
    amplify.publish('eventUpdated');
    amplify.publish('eventImportUpdated');
    modal.toastDone('aiimport.undo.done');
  }
});

//drag anywhere over the calendar: the overlay of the page takes the drop, a modal open has its own drop targets
registerFileDropConsumer(async function(){
  const overlay = document.querySelector<HTMLElement>('.aiimport-dropoverlay');
  if(!overlay || document.querySelector('.modal-container')){
    return {nodes:[], ondrop(){}};
  }
  return {
    nodes:[overlay],
    ondrop(event :DragEvent){
      const file = event.dataTransfer.files[0];
      if(file) importDroppedFile(file);
    }
  };
});
